import { useMemo } from "react";

import { DataTable } from "components/ui/DataTable";
import { Pill, PILL_COLORS } from "components/ui/Pill";
import { useVaccineRefusalReport } from "features/immunizations/hooks/useVaccineRefusals";
import {
  isClinicalNonAdministration,
  refusalOutcomeLabel,
} from "features/immunizations/utils/vaccineRefusal";
import { formatDateWithDashes } from "utils/dateFormatter";

/**
 * The practice-level follow-up list: who declined what, over a date range (KIN-1849).
 *
 * Withdrawn records are kept in the list rather than filtered out. The parent who
 * reconsidered is exactly who a follow-up call is for, and dropping them would make the
 * list quietly disagree with the patient screen.
 *
 * The patient is identified by id, not by name: PatientVaccineRefusalDTO carries only
 * patientId, so a name would have to be fetched per row. Raised on the ticket rather than
 * worked around here, because a follow-up list nobody can read is not much use.
 */
export const DeclinedVaccinesTable = ({ range }) => {
  const {
    data: refusals = [],
    isLoading,
    isError,
    refetch,
  } = useVaccineRefusalReport(range);

  const rows = useMemo(
    () =>
      [...refusals].sort((left, right) =>
        String(right.refusedOn || "").localeCompare(String(left.refusedOn || ""))
      ),
    [refusals]
  );

  const columns = useMemo(
    () => [
      {
        id: "refusedOn",
        header: "Date",
        width: "110px",
        render: (item) => formatDateWithDashes(item.refusedOn) || "-",
      },
      {
        id: "patientId",
        header: "Patient ID",
        width: "110px",
        render: (item) => item.patientId ?? "-",
      },
      {
        id: "vaccineName",
        header: "Vaccine",
        width: "220px",
        render: (item) => item.vaccineName || "-",
      },
      {
        id: "outcome",
        header: "Outcome",
        width: "170px",
        // A refusal and a clinical withholding are different events — HL7 table 0322
        // separates RE from NA, and the HSE notifies them separately — so they do not
        // share a colour either.
        render: (item) => {
          const colors = isClinicalNonAdministration(item)
            ? PILL_COLORS.reactionUnknown
            : PILL_COLORS.reactionNo;

          return (
            <Pill color={colors.color} bg={colors.bg} border={colors.border}>
              {refusalOutcomeLabel(item.outcome)}
            </Pill>
          );
        },
      },
      {
        id: "reason",
        header: "Reason",
        width: "260px",
        render: (item) => item.refusalReasonDescription || "-",
      },
      {
        id: "status",
        header: "Status",
        width: "130px",
        render: (item) => {
          const colors = item.withdrawn
            ? PILL_COLORS.reactionUnknown
            : PILL_COLORS.sentYes;

          return (
            <Pill color={colors.color} bg={colors.bg} border={colors.border}>
              {item.withdrawn ? "Withdrawn" : "Standing"}
            </Pill>
          );
        },
      },
    ],
    []
  );

  return (
    <DataTable
      columns={columns}
      data={rows}
      getRowId={(row) => row.id}
      isLoading={isLoading}
      isError={isError}
      onRetry={refetch}
      errorLabel="Could not load the declined vaccines."
      emptyTitle="Nothing declined or withheld"
      emptyText="No vaccine was declined or withheld in this period. Widen the date range to look further back."
    />
  );
};
